import React, { useState, useEffect } from 'react';
import { LayoutDashboard, CalendarCheck, MessageSquare, Compass, Home, LogOut, Users, Bell, Plus, X, Trash2, Edit3 } from 'lucide-react';

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('Overview');
  const [showAddStay, setShowAddStay] = useState(false);
  const [showAddDiscover, setShowAddDiscover] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [tempGallery, setTempGallery] = useState([]);
  
  const [stays, setStays] = useState([]);
  const [discoverItems, setDiscoverItems] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [loading, setLoading] = useState(false);
  const [adminProfile, setAdminProfile] = useState({ name: '', email: '', phone: '', avatar: '' });
  const [profileImage, setProfileImage] = useState(localStorage.getItem('adminAvatar') || '');


  const [stats, setStats] = useState([
    { title: 'Confirmed Bookings', value: '0', icon: <CalendarCheck size={20} />, color: 'bg-emerald-500' },
    { title: 'Active Inquiries', value: '0', icon: <MessageSquare size={20} />, color: 'bg-blue-500' },
    { title: 'Confirmed Guests', value: '0', icon: <Users size={20} />, color: 'bg-amber-500' },
  ]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchData = async () => {
    const headers = getAuthHeaders();
    try {
      // Helper function to fetch safely
      const safeFetch = async (url, options = {}) => {
        try {
          const res = await fetch(url, options);
          if (!res.ok) return [];
          return await res.json();
        } catch (e) {
          console.error(`Error fetching ${url}:`, e);
          return [];
        }
      };

      const stayData = await safeFetch(`https://jaladhara.onrender.com/api/stay`);
      const discoverData = await safeFetch(`https://jaladhara.onrender.com/api/discover`);
      const resData = await safeFetch(`https://jaladhara.onrender.com/api/reservations`, { headers });
      const inqData = await safeFetch(`https://jaladhara.onrender.com/api/inquiries`, { headers });
      const notifData = await safeFetch(`https://jaladhara.onrender.com/api/notifications`, { headers });
      const profileData = await safeFetch(`https://jaladhara.onrender.com/api/admin/profile`, { headers });

      console.log('Stays received:', stayData);
      setStays(Array.isArray(stayData) ? stayData : []);
      setDiscoverItems(Array.isArray(discoverData) ? discoverData : []);
      setReservations(Array.isArray(resData) ? resData : []);
      setInquiries(Array.isArray(inqData) ? inqData : []);
      if (profileData && !profileData.length) setAdminProfile(profileData);
      
      if (Array.isArray(notifData) && notifData.length > notifications.length && notifications.length > 0) {
        const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3');
        audio.play().catch(e => console.log('Sound error:', e));
      }
      setNotifications(Array.isArray(notifData) ? notifData : []);

      // Update Stats
      const confirmedReservations = Array.isArray(resData) ? resData.filter(r => r.status === 'Confirmed') : [];

      setStats([
        { title: 'Confirmed Bookings', value: confirmedReservations.length.toString(), icon: <CalendarCheck size={20} />, color: 'bg-emerald-500' },
        { title: 'Active Inquiries', value: inqData.length.toString(), icon: <MessageSquare size={20} />, color: 'bg-blue-500' },
        { title: 'Confirmed Guests', value: (confirmedReservations.length * 2).toString(), icon: <Users size={20} />, color: 'bg-amber-500' },
      ]);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const [uploadingImage, setUploadingImage] = useState(false);

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingImage(true);
    
    for (const file of files) {
      const formData = new FormData();
      formData.append('image', file);

      try {
        const res = await fetch(`https://jaladhara.onrender.com/api/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` },
          body: formData
        });
        const data = await res.json();
        if (data.url) {
          setTempGallery(prev => [...prev, data.url]);
        }
      } catch (err) {
        console.error('Upload failed:', err);
      }
    }
    setUploadingImage(false);
  };

  const addImageToTemp = () => {
    document.getElementById('fileInput').click();
  };

  const removeImageFromTemp = (index) => setTempGallery(tempGallery.filter((_, i) => i !== index));

  const handleAddStay = async (e) => {
    e.preventDefault();
    setLoading(true);
    const formData = new FormData(e.target);
    const itemData = {
      title: formData.get('title'),
      desc: formData.get('desc'),
      gallery: tempGallery.length > 0 ? tempGallery : ['https://images.unsplash.com/photo-1501785888041-af3ef285b470?auto=format&fit=crop&q=80&w=400']
    };

    try {
      const url = editingItem ? `https://jaladhara.onrender.com/api/stay/${editingItem._id || editingItem.id}` : `https://jaladhara.onrender.com/api/stay`;
      const method = editingItem ? 'PATCH' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(itemData)
      });

      if (res.ok) {
        fetchData();
        closeModals();
      }
    } catch (error) {
      console.error('Error saving stay:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDiscover = async (e) => {
    e.preventDefault();
    setLoading(true);
    const formData = new FormData(e.target);
    const itemData = {
      title: formData.get('title'),
      category: formData.get('category'),
      gallery: tempGallery.length > 0 ? tempGallery : ['https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&q=80&w=400']
    };

    try {
      const url = editingItem ? `https://jaladhara.onrender.com/api/discover/${editingItem._id || editingItem.id}` : `https://jaladhara.onrender.com/api/discover`;
      const method = editingItem ? 'PATCH' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(itemData)
      });

      if (res.ok) {
        fetchData();
        closeModals();
      }
    } catch (error) {
      console.error('Error saving wonder:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStay = async (id) => {
    if (!window.confirm('Delete this accommodation?')) return;
    try {
      await fetch(`https://jaladhara.onrender.com/api/stay/${id}`, { 
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting stay:', error);
    }
  };

  const handleDeleteDiscover = async (id) => {
    if (!window.confirm('Delete this forest wonder?')) return;
    try {
      await fetch(`https://jaladhara.onrender.com/api/discover/${id}`, { 
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting wonder:', error);
    }
  };

  const handleDeleteReservation = async (id) => {
    if (!window.confirm('Delete this reservation?')) return;
    try {
      await fetch(`https://jaladhara.onrender.com/api/reservations/${id}`, { 
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting reservation:', error);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await fetch(`https://jaladhara.onrender.com/api/reservations/${id}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: newStatus })
      });
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleDeleteInquiry = async (id) => {
    if (!window.confirm('Delete this inquiry?')) return;
    try {
      await fetch(`https://jaladhara.onrender.com/api/inquiries/${id}`, { 
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting inquiry:', error);
    }
  };

  const closeModals = () => {
    setShowAddStay(false);
    setShowAddDiscover(false);
    setEditingItem(null);
    setTempGallery([]);
  };

  const markAllAsRead = async () => {
    try {
      const res = await fetch(`https://jaladhara.onrender.com/api/notifications/read-all`, {
        method: 'PATCH',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      }
    } catch (err) {
      console.error('Failed to mark notifications as read:', err);
    }
  };

  const handleProfileImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    try {
      const res = await fetch(`https://jaladhara.onrender.com/api/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` },
        body: formData
      });
      const data = await res.json();
      setProfileImage(data.url);
      localStorage.setItem('adminAvatar', data.url);
    } catch (err) {
      console.error('Upload failed:', err);
    }
  };

  const updateProfileData = async (data) => {
    try {
      const res = await fetch(`https://jaladhara.onrender.com/api/admin/profile`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      if (res.ok) {
        fetchData();
      }
    } catch (err) {
      console.error('Update failed:', err);
    }
  };

  const clearAllNotifications = async () => {
    try {
      const res = await fetch(`https://jaladhara.onrender.com/api/notifications/clear-all`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        setNotifications([]);
      }
    } catch (err) {
      console.error('Failed to clear notifications:', err);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());
    if (data.password === '') delete data.password;
    await updateProfileData(data);
    alert('Profile updated successfully!');
  };

  const handleDeleteNotification = async (id) => {
    try {
      const res = await fetch(`https://jaladhara.onrender.com/api/notifications/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        setNotifications(prev => prev.filter(n => (n._id || n.id) !== id));
      }
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    window.location.href = '/login';
  };

  const renderOverview = () => (
    <div className="space-y-10">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, idx) => (
          <div key={idx} className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm hover:shadow-md transition-shadow group">
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 rounded-2xl ${stat.color} bg-opacity-10 flex items-center justify-center ${stat.color.replace('bg-', 'text-')}`}>
                {stat.icon}
              </div>
            </div>
            <h3 className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-1">{stat.title}</h3>
            <p className="text-2xl font-black text-gray-800 tracking-tight">{stat.value}</p>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-[40px] border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-8 border-b border-gray-50 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-800">Confirmed Bookings (Recent)</h3>
          <button onClick={() => setActiveTab('Reservations')} className="text-sm font-bold text-emerald-600 hover:text-emerald-700 underline underline-offset-4 transition-colors">View All</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-50/50 text-left">
                <th className="px-6 py-5 text-[10px] uppercase font-bold text-gray-400 tracking-widest pl-10">ID</th>
                <th className="px-6 py-5 text-[10px] uppercase font-bold text-gray-400 tracking-widest">Guest</th>
                <th className="px-6 py-5 text-[10px] uppercase font-bold text-gray-400 tracking-widest">Contact</th>
                <th className="px-6 py-5 text-[10px] uppercase font-bold text-gray-400 tracking-widest">Stay</th>
                <th className="px-6 py-5 text-[10px] uppercase font-bold text-gray-400 tracking-widest whitespace-nowrap">Arrival</th>
                <th className="px-6 py-5 text-[10px] uppercase font-bold text-gray-400 tracking-widest whitespace-nowrap">Departure</th>
                <th className="px-6 py-5 text-[10px] uppercase font-bold text-gray-400 tracking-widest">Guests</th>
                <th className="px-6 py-5 text-[10px] uppercase font-bold text-gray-400 tracking-widest">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {Array.isArray(reservations) && reservations
                .filter(bk => bk.status === 'Confirmed')
                .slice(0, 5)
                .map((bk, idx) => (
                <tr key={idx} className="hover:bg-gray-50/30 transition-colors group">
                  <td className="px-6 py-6 font-mono text-[11px] text-gray-400 pl-10">#{(bk._id || bk.id).slice(-6)}</td>
                  <td className="px-6 py-6">
                    <div className="text-sm font-bold text-gray-800">{bk.guest}</div>
                  </td>
                  <td className="px-6 py-6">
                    <div className="text-xs font-bold text-gray-700">{bk.phone}</div>
                    <div className="text-[10px] text-gray-400">{bk.email}</div>
                  </td>
                  <td className="px-6 py-6">
                    <span className="px-3 py-1 bg-gray-100 rounded-lg text-xs font-medium text-gray-600">{bk.stay}</span>
                  </td>
                  <td className="px-6 py-6 text-sm text-emerald-600 font-bold whitespace-nowrap">{bk.checkIn}</td>
                  <td className="px-6 py-6 text-sm text-red-400 font-bold whitespace-nowrap">{bk.checkOut}</td>
                  <td className="px-6 py-6 text-sm font-bold text-gray-600">{bk.guests}</td>
                  <td className="px-6 py-6">
                    <span className="px-3 py-1 bg-emerald-50 text-emerald-600 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-sm">
                      {bk.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {renderInquiries()}
    </div>
  );

  const renderStays = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-bold text-gray-800 tracking-tight">Accommodation Management</h3>
        <button onClick={() => { setTempGallery([]); setEditingItem(null); setShowAddStay(true); }} className="bg-emerald-600 text-white px-6 py-3 rounded-2xl text-sm font-bold flex items-center gap-2 hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-900/20"><Plus size={18} /> Add New Stay</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {stays.map((stay) => (
          <div key={stay._id || stay.id} className="bg-white rounded-[32px] border border-gray-100 shadow-sm overflow-hidden group hover:shadow-xl transition-all duration-500">
            <div className="h-48 bg-gray-100 relative">
              <img src={stay.gallery?.[0] || 'https://via.placeholder.com/400x300?text=No+Image'} alt={stay.title} className="w-full h-full object-cover" />
              {stay.gallery?.length > 1 && <div className="absolute bottom-4 right-4 bg-black/50 backdrop-blur-sm px-2 py-1 rounded-lg text-[8px] font-bold text-white">+{stay.gallery.length - 1} More</div>}
            </div>
            <div className="p-6">
              <h4 className="font-bold text-gray-800 mb-2">{stay.title}</h4>
              <p className="text-xs text-gray-400 line-clamp-2 leading-relaxed">{stay.desc}</p>
              <div className="mt-6 flex gap-3">
                <button onClick={() => { setEditingItem(stay); setTempGallery(stay.gallery || []); setShowAddStay(true); }} className="flex-1 py-2 bg-emerald-50 text-emerald-600 rounded-xl text-[10px] font-bold uppercase tracking-widest hover:bg-emerald-600 hover:text-white transition-all flex items-center justify-center gap-2"><Edit3 size={12} /> Edit Details</button>
                <button onClick={() => handleDeleteStay(stay._id || stay.id)} className="p-2 text-red-300 hover:text-red-500 transition-colors"><Trash2 size={16} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderDiscover = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-bold text-gray-800 tracking-tight">Forest Wonders Content</h3>
        <button onClick={() => { setTempGallery([]); setEditingItem(null); setShowAddDiscover(true); }} className="bg-emerald-600 text-white px-6 py-3 rounded-2xl text-sm font-bold flex items-center gap-2 hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-900/20"><Plus size={18} /> Add New Wonder</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {discoverItems.map((item) => (
          <div key={item._id || item.id} className="bg-white rounded-[32px] border border-gray-100 shadow-sm overflow-hidden group hover:shadow-lg transition-all duration-500">
            <div className="h-40 bg-gray-100 relative">
              <img src={item.gallery?.[0] || 'https://via.placeholder.com/400x300?text=No+Image'} alt={item.title} className="w-full h-full object-cover" />
              {item.gallery?.length > 1 && <div className="absolute bottom-3 right-3 bg-black/50 backdrop-blur-sm px-2 py-1 rounded-lg text-[8px] font-bold text-white">+{item.gallery.length - 1}</div>}
            </div>
            <div className="p-5">
              <span className="text-[9px] font-black uppercase tracking-widest text-emerald-600 mb-1 block">{item.category}</span>
              <h4 className="font-bold text-gray-800 text-sm mb-4">{item.title}</h4>
              <div className="flex items-center justify-between border-t border-gray-50 pt-4">
                <button onClick={() => { setEditingItem(item); setTempGallery(item.gallery || []); setShowAddDiscover(true); }} className="text-[10px] font-bold text-gray-400 hover:text-emerald-600 uppercase tracking-widest flex items-center gap-1"><Edit3 size={12} /> Edit</button>
                <button onClick={() => handleDeleteDiscover(item._id || item.id)} className="text-red-200 hover:text-red-500 transition-colors"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderReservations = () => (
    <div className="bg-white rounded-[40px] border border-gray-100 shadow-sm overflow-hidden">
      <div className="p-8 border-b border-gray-50 flex items-center justify-between">
        <h3 className="text-lg font-bold text-gray-800">Reservation Management</h3>
        <input type="text" placeholder="Search guests..." className="bg-gray-50 border border-gray-100 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-gray-50/50 text-left">
              <th className="px-6 py-5 text-[10px] uppercase font-bold text-gray-400 tracking-widest pl-10">ID</th>
              <th className="px-6 py-5 text-[10px] uppercase font-bold text-gray-400 tracking-widest">Guest</th>
              <th className="px-6 py-5 text-[10px] uppercase font-bold text-gray-400 tracking-widest">Contact</th>
              <th className="px-6 py-5 text-[10px] uppercase font-bold text-gray-400 tracking-widest">Stay</th>
              <th className="px-6 py-5 text-[10px] uppercase font-bold text-gray-400 tracking-widest whitespace-nowrap">Arrival</th>
              <th className="px-6 py-5 text-[10px] uppercase font-bold text-gray-400 tracking-widest whitespace-nowrap">Departure</th>
              <th className="px-6 py-5 text-[10px] uppercase font-bold text-gray-400 tracking-widest">Guests</th>
              <th className="px-6 py-5 text-[10px] uppercase font-bold text-gray-400 tracking-widest">Status</th>
              <th className="px-6 py-5 text-[10px] uppercase font-bold text-gray-400 tracking-widest text-right pr-10">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {reservations.map((bk, idx) => (
              <tr key={idx} className="hover:bg-gray-50/30 transition-colors group">
                <td className="px-6 py-6 font-mono text-[11px] text-gray-400 pl-10">#{(bk._id || bk.id).slice(-6)}</td>
                <td className="px-6 py-6">
                  <div className="text-sm font-bold text-gray-800">{bk.guest}</div>
                </td>
                <td className="px-6 py-6">
                  <div className="text-xs font-bold text-gray-700">{bk.phone}</div>
                  <div className="text-[10px] text-gray-400">{bk.email}</div>
                </td>
                <td className="px-6 py-6">
                  <span className="px-3 py-1 bg-gray-100 rounded-lg text-xs font-medium text-gray-600">{bk.stay}</span>
                </td>
                <td className="px-6 py-6 text-sm text-emerald-600 font-bold whitespace-nowrap">{bk.checkIn}</td>
                <td className="px-6 py-6 text-sm text-red-400 font-bold whitespace-nowrap">{bk.checkOut}</td>
                <td className="px-6 py-6 text-sm font-bold text-gray-600">{bk.guests}</td>
                <td className="px-6 py-6">
                  <select 
                    value={bk.status} 
                    onChange={(e) => handleStatusChange(bk._id || bk.id, e.target.value)}
                    className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest border-none focus:ring-4 focus:ring-emerald-500/10 cursor-pointer transition-all shadow-sm ${
                      bk.status === 'Confirmed' ? 'bg-emerald-50 text-emerald-600' : 
                      bk.status === 'Rejected' ? 'bg-red-50 text-red-600' : 
                      'bg-amber-50 text-amber-600'
                    }`}
                  >
                    <option value="Pending">Pending</option>
                    <option value="Confirmed">Confirm</option>
                    <option value="Rejected">Reject</option>
                  </select>
                </td>
                <td className="px-6 py-6 text-right pr-10">
                  <button onClick={() => handleDeleteReservation(bk._id || bk.id)} className="p-2 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all">
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderInquiries = () => (
    <div className="bg-white rounded-[40px] border border-gray-100 shadow-sm overflow-hidden mt-10">
      <div className="p-8 border-b border-gray-50 flex items-center justify-between">
        <h3 className="text-lg font-bold text-gray-800">Guest Inquiries</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-gray-50/50">
              <th className="px-8 py-4 text-[10px] uppercase font-bold text-gray-400 tracking-widest">Name</th>
              <th className="px-8 py-4 text-[10px] uppercase font-bold text-gray-400 tracking-widest">Email</th>
              <th className="px-8 py-4 text-[10px] uppercase font-bold text-gray-400 tracking-widest">Subject</th>
              <th className="px-8 py-4 text-[10px] uppercase font-bold text-gray-400 tracking-widest">Message</th>
              <th className="px-8 py-4 text-[10px] uppercase font-bold text-gray-400 tracking-widest">Date</th>
              <th className="px-8 py-4 text-[10px] uppercase font-bold text-gray-400 tracking-widest text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {inquiries.map((inq, idx) => (
              <tr key={idx} className="hover:bg-gray-50/30 transition-colors">
                <td className="px-8 py-6 font-bold text-gray-800 text-sm">
                  {inq.name}
                  <div className="text-[10px] text-emerald-600 font-bold">{inq.phone}</div>
                </td>
                <td className="px-8 py-6 text-sm text-gray-700">{inq.email}</td>
                <td className="px-8 py-6 text-sm text-gray-500">{inq.subject}</td>
                <td className="px-8 py-6 text-sm text-gray-400 italic max-w-xs truncate">{inq.message}</td>
                <td className="px-8 py-6 text-[10px] text-gray-400 font-bold">{inq.date}</td>
                <td className="px-8 py-6 text-right">
                  <button onClick={() => handleDeleteInquiry(inq._id || inq.id)} className="text-red-300 hover:text-red-500 transition-colors">
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderProfile = () => (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-[40px] border border-gray-100 shadow-xl overflow-hidden animate-in fade-in zoom-in-95 duration-500">
        <div className="h-48 bg-[#022c22] relative overflow-hidden">
          <div className="absolute inset-0 bg-emerald-500/10 blur-3xl rounded-full translate-x-1/2"></div>
        </div>
        <div className="px-12 pb-12">
          <div className="relative -mt-24 mb-10 inline-block">
            <div className="relative group">
              <input type="file" onChange={handleProfileImageUpload} className="absolute inset-0 opacity-0 cursor-pointer z-10" />
              <div className="w-40 h-40 rounded-[48px] bg-white p-2 shadow-2xl border border-gray-50">
                <div className="w-full h-full rounded-[40px] bg-emerald-100 flex items-center justify-center text-emerald-700 text-5xl font-black overflow-hidden relative">
                  {profileImage ? <img src={profileImage} alt="profile" className="w-full h-full object-cover" /> : adminProfile.name?.charAt(0) || 'A'}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-xs font-bold uppercase tracking-widest">Change Photo</div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-12">
            <h3 className="text-3xl font-black text-gray-800 tracking-tight">{adminProfile.name}</h3>
            <p className="text-gray-400 font-bold uppercase text-xs tracking-[0.3em] mt-2">Administrative Profile</p>
          </div>

          <form onSubmit={handleUpdateProfile} className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-2">
              <label className="text-[10px] uppercase font-black text-gray-400 tracking-widest ml-1">Full Name</label>
              <input name="name" defaultValue={adminProfile.name} className="w-full bg-gray-50 border border-gray-100 rounded-2xl px-6 py-4 focus:outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500/50 transition-all font-bold text-gray-800" />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] uppercase font-black text-gray-400 tracking-widest ml-1">Phone Number</label>
              <input name="phone" defaultValue={adminProfile.phone} className="w-full bg-gray-50 border border-gray-100 rounded-2xl px-6 py-4 focus:outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500/50 transition-all font-bold text-gray-800" />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] uppercase font-black text-gray-400 tracking-widest ml-1">Email Address</label>
              <input name="email" defaultValue={adminProfile.email} className="w-full bg-gray-50 border border-gray-100 rounded-2xl px-6 py-4 focus:outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500/50 transition-all font-bold text-gray-800" />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] uppercase font-black text-gray-400 tracking-widest ml-1">New Password</label>
              <input name="password" type="password" placeholder="Leave blank to keep current" className="w-full bg-gray-50 border border-gray-100 rounded-2xl px-6 py-4 focus:outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500/50 transition-all font-bold text-gray-800" />
            </div>
            
            <div className="col-span-1 md:col-span-2 pt-6">
              <button type="submit" className="bg-emerald-600 hover:bg-emerald-500 text-white font-black py-5 px-10 rounded-2xl transition-all duration-300 shadow-xl shadow-emerald-900/20 uppercase text-xs tracking-[0.3em]">Save Profile Updates</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'Overview': return renderOverview();
      case 'Reservations': return renderReservations();
      case 'Stays': return renderStays();
      case 'Discover': return renderDiscover();
      case 'Inquiries': return renderInquiries();
      case 'Profile': return renderProfile();
      default: return renderOverview();
    }
  };

  return (
    <div className="flex h-screen bg-gray-50 font-sans">
      <div className="w-64 bg-[#022c22] text-white flex flex-col p-6 shadow-2xl">
        <div className="flex items-center gap-3 mb-10">
          <h1 className="text-xl font-bold tracking-tight">Jaladhara Tourism<span className="text-emerald-500">.</span></h1>
        </div>
        <nav className="flex-1 space-y-2">
          {[
            { id: 'Overview', icon: <LayoutDashboard size={20} /> }, 
            { id: 'Reservations', icon: <CalendarCheck size={20} /> }, 
            { id: 'Inquiries', icon: <MessageSquare size={20} /> },
            { id: 'Stays', icon: <Home size={20} /> }, 
            { id: 'Discover', icon: <Compass size={20} /> }
          ].map((item) => (
            <button key={item.id} onClick={() => setActiveTab(item.id)} className={`w-full flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 ${activeTab === item.id ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-900/50' : 'text-emerald-100/50 hover:bg-white/5 hover:text-white'}`}>
              {item.icon} <span className="font-medium text-sm">{item.id}</span>
            </button>
          ))}
        </nav>
        <button onClick={handleLogout} className="mt-auto flex items-center gap-4 px-4 py-3 text-red-400 hover:bg-red-500/10 rounded-2xl transition-all"><LogOut size={20} /> <span className="font-medium text-sm">Logout</span></button>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-20 bg-white border-b border-gray-100 flex items-center justify-between px-10 shrink-0">
          <div><h2 className="text-xl font-bold text-gray-800">{activeTab}</h2><p className="text-xs text-gray-400 font-medium uppercase tracking-widest mt-1">Admin Management</p></div>
          <div className="flex items-center gap-6">
            <div className="relative">
              <button 
                onClick={() => {
                  setShowNotifications(!showNotifications);
                  if (!showNotifications) fetchData(); // Refresh on open
                }}
                className={`relative w-10 h-10 rounded-full flex items-center justify-center transition-all ${showNotifications ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-200' : 'bg-gray-50 text-gray-400 hover:text-emerald-600'}`}
              >
                <Bell size={20} />
                {Array.isArray(notifications) && notifications.some(n => !n.isRead) && (
                  <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full border-2 border-white animate-pulse"></span>
                )}
              </button>

              {showNotifications && (
                <div className="absolute right-0 mt-4 w-80 bg-white rounded-3xl shadow-2xl border border-gray-100 z-[300] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-300">
                  <div className="p-5 border-b border-gray-50 flex items-center justify-between bg-gray-50/50">
                    <h4 className="text-sm font-bold text-gray-800">Notifications</h4>
                    <div className="flex items-center gap-3">
                      <button onClick={markAllAsRead} className="text-[10px] font-bold text-emerald-600 hover:text-emerald-700 uppercase tracking-widest">Mark all as read</button>
                      <span className="w-1 h-1 bg-gray-200 rounded-full"></span>
                      <button onClick={clearAllNotifications} className="text-[10px] font-bold text-red-400 hover:text-red-500 uppercase tracking-widest">Clear all</button>
                      <button onClick={() => setShowNotifications(false)} className="text-gray-400 hover:text-gray-600"><X size={14} /></button>
                    </div>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-10 text-center text-gray-400 text-xs italic">No notifications yet</div>
                    ) : (
                      notifications.map((n, idx) => (
                        <div key={idx} className={`p-5 border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors relative group/item ${!n.isRead ? 'bg-emerald-50/20' : ''}`}>
                          {!n.isRead && <div className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-500"></div>}
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteNotification(n._id || n.id);
                            }}
                            className="absolute top-4 right-4 opacity-0 group-hover/item:opacity-100 p-1 hover:bg-red-50 text-gray-300 hover:text-red-500 rounded-lg transition-all"
                          >
                            <Trash2 size={12} />
                          </button>
                          <div className="flex gap-4">
                            <div className={`w-8 h-8 rounded-xl shrink-0 flex items-center justify-center ${n.type === 'Booking' ? 'bg-blue-100 text-blue-600' : 'bg-amber-100 text-amber-600'}`}>
                              {n.type === 'Booking' ? <CalendarCheck size={14} /> : <MessageSquare size={14} />}
                            </div>
                            <div>
                              <p className="text-xs font-bold text-gray-800 mb-1">{n.title}</p>
                              <p className="text-[10px] text-gray-500 leading-relaxed">{n.message}</p>
                              <p className="text-[8px] text-gray-300 mt-2 font-bold uppercase">{new Date(n.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="relative group">
              <input type="file" onChange={handleProfileImageUpload} className="absolute inset-0 opacity-0 cursor-pointer z-10" />
              <div className="w-10 h-10 rounded-full bg-emerald-100 border border-emerald-200 flex items-center justify-center text-emerald-700 font-bold shadow-sm overflow-hidden group-hover:border-emerald-500 transition-all">
                {profileImage ? <img src={profileImage} alt="avatar" className="w-full h-full object-cover" /> : 'A'}
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Plus size={12} className="text-white" />
                </div>
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-10 bg-gray-50/50">
          <div className="max-w-7xl mx-auto">
            {renderContent()}
          </div>
        </main>
      </div>

      {showAddStay && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-black/60 backdrop-blur-sm">
          <div className="bg-white w-full max-w-2xl rounded-[40px] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
            <div className="p-8 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-800">{editingItem ? 'Edit Accommodation' : 'Add New Accommodation'}</h3>
              <button onClick={closeModals} className="text-gray-400 hover:text-gray-600 transition-colors"><X /></button>
            </div>
            <form onSubmit={handleAddStay} className="p-8 space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="col-span-2"><label className="block text-[10px] uppercase font-bold text-gray-400 mb-2 tracking-widest">Stay Title</label><input name="title" defaultValue={editingItem?.title} required type="text" className="w-full bg-gray-50 border border-gray-100 rounded-2xl px-5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" /></div>
                <div className="col-span-2"><label className="block text-[10px] uppercase font-bold text-gray-400 mb-2 tracking-widest">Gallery Images</label>
                  <div className="flex flex-wrap gap-3 mb-4">
                    {tempGallery.map((img, idx) => (<div key={idx} className="relative w-20 h-20 rounded-xl overflow-hidden border border-emerald-100 group"><img src={img} alt="temp" className="w-full h-full object-cover" /><button type="button" onClick={() => removeImageFromTemp(idx)} className="absolute inset-0 bg-red-500/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-white"><Trash2 size={16} /></button></div>))}
                    <button type="button" onClick={addImageToTemp} disabled={uploadingImage} className="w-20 h-20 rounded-xl border-2 border-dashed border-emerald-100 flex flex-col items-center justify-center text-emerald-600 hover:bg-emerald-50 hover:border-emerald-300 transition-all">
                      <Plus size={20} />
                      <span className="text-[8px] font-bold mt-1">{uploadingImage ? 'Uploading...' : 'Add Image'}</span>
                    </button>
                  </div>
                </div>
                <div className="col-span-2"><label className="block text-[10px] uppercase font-bold text-gray-400 mb-2 tracking-widest">Description</label><textarea name="desc" defaultValue={editingItem?.desc} rows="3" className="w-full bg-gray-50 border border-gray-100 rounded-2xl px-5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"></textarea></div>
              </div>
              <button type="submit" disabled={loading} className="w-full bg-emerald-600 text-white font-bold py-4 rounded-2xl shadow-xl shadow-emerald-900/20 hover:bg-emerald-700 transition-all uppercase tracking-widest text-xs disabled:opacity-50">{loading ? 'Saving...' : (editingItem ? 'Update Accommodation' : 'Publish Accommodation')}</button>
            </form>
          </div>
        </div>
      )}

      {showAddDiscover && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-black/60 backdrop-blur-sm">
          <div className="bg-white w-full max-w-md rounded-[40px] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
            <div className="p-8 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-800">{editingItem ? 'Edit Wonder' : 'Add New Wonder'}</h3>
              <button onClick={closeModals} className="text-gray-400 hover:text-gray-600 transition-colors"><X /></button>
            </div>
            <form onSubmit={handleAddDiscover} className="p-8 space-y-5">
              <div><label className="block text-[10px] uppercase font-bold text-gray-400 mb-2 tracking-widest">Spot Title</label><input name="title" defaultValue={editingItem?.title} required type="text" className="w-full bg-gray-50 border border-gray-100 rounded-2xl px-5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" /></div>
              <div><label className="block text-[10px] uppercase font-bold text-gray-400 mb-2 tracking-widest">Category</label><select name="category" defaultValue={editingItem?.category} className="w-full bg-gray-50 border border-gray-100 rounded-2xl px-5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"><option>Waterfall</option><option>Viewpoint</option><option>Nature</option></select></div>
              <div><label className="block text-[10px] uppercase font-bold text-gray-400 mb-2 tracking-widest">Photos Gallery</label>
                <div className="flex flex-wrap gap-3 mt-2">
                  {tempGallery.map((img, idx) => (<div key={idx} className="relative w-16 h-16 rounded-xl overflow-hidden border border-emerald-100 group"><img src={img} alt="temp" className="w-full h-full object-cover" /><button type="button" onClick={() => removeImageFromTemp(idx)} className="absolute inset-0 bg-red-500/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-white"><Trash2 size={12} /></button></div>))}
                  <button type="button" onClick={addImageToTemp} disabled={uploadingImage} className="w-16 h-16 rounded-xl border-2 border-dashed border-emerald-100 flex flex-col items-center justify-center text-emerald-600 hover:bg-emerald-50 transition-all">
                    <Plus size={16} />
                    <span className="text-[8px] font-bold mt-1">{uploadingImage ? '...' : ''}</span>
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading} className="w-full bg-emerald-600 text-white font-bold py-4 rounded-2xl shadow-xl shadow-emerald-900/20 hover:bg-emerald-700 transition-all uppercase tracking-widest text-xs disabled:opacity-50">{loading ? 'Saving...' : (editingItem ? 'Update Wonder' : 'Publish Wonder')}</button>
            </form>
          </div>
        </div>
      )}
      {/* Hidden File Input */}
      <input 
        type="file" 
        id="fileInput" 
        className="hidden" 
        accept="image/*" 
        multiple
        onChange={handleImageUpload} 
      />
    </div>
  );
};

export default Dashboard;
